import { booleanAttribute, computed, DestroyRef, Directive, inject, input, OnDestroy, OnInit } from '@angular/core';
import { outputFromObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReplaySubject, tap } from 'rxjs';
import { isBlank } from '../../core/utils';
import { DevTableConfig, DevTableConfigValues } from '../model/table-model';
import { HisTableService } from '../service/his-table.service';
import { HisTableMetaDirective } from './his-table-meta.directive';

/**
 * Provides a table configuration context for `HisTableService`-backed state management.\
 * Components and directives within its scope can read and mutate the shared `tableConfig` signal.
 *
 * ---
 *
 * ### Identity and sharing
 * - **No `hisTableId`** -> registers as an anonymous table; configuration is not shared with any other context implicitly.
 * - **With `hisTableId`** -> configuration is shared across all contexts bearing the same ID.\
 *   The first context to register defines the default values; subsequent registrations with the same ID reuse the existing state.
 *   Context registration order is deterministic through the template hierarchy.
 *
 * ### Nesting
 * When a context is detected on an ancestor element it is automatically inherited. The nested instance
 * becomes a transparent delegate and does **not** register an independent table with the service.\
 * A nested context with a **different** `hisTableId` is treated as a new, independent context.
 *
 * ### Persistence
 * Enabled by default when a `hisTableId` is provided. Suppress it with the `disablePersist` attribute.
 *
 * ---
 *
 * ### Usage
 *
 * **a) Wrapper:** Single context node wrapping multiple table components:
 * ```html
 * <ng-container hisTableId="my.table" #ctx="hisTableContext"
 *   [defaultConfig]="{ paginatorPosition: 'top', defaultTablePageSize: 20 }">
 *   <his-table-config [columns]="columns"></his-table-config>
 *   <p-table [hisColumnState]="columns" [value]="data"
 *     [paginator]="true" [paginatorPosition]="ctx.tableConfig().paginatorPosition">
 *   </p-table>
 * </ng-container>
 * ```
 *
 * **b) Explicit:** Directive placed directly on each participating component, linked by ID:
 * ```html
 * <his-table-config hisTableId="my.table" [columns]="columns"
 *   [defaultConfig]="{ paginatorPosition: 'top', defaultTablePageSize: 20 }">
 * </his-table-config>
 * <p-table hisTableId="my.table" #ctx="hisTableContext"
 *   [hisColumnState]="columns" [value]="data"
 *   [paginator]="true" [paginatorPosition]="ctx.tableConfig().paginatorPosition">
 * </p-table>
 * ```
 * > `defaultConfig` only takes effect from the first context to register; here `his-table-config`
 * 
 * ---
 * 
 * ### HisTableMetaDirective Interop
 * 
 * The `HisTableMetaDirective` can be used in conjunction with the table context to provide additional
 * metadata and behavior for the table. It is attached to any _p-table_ by default.
 * 
 * Any changes to the table configuration will be automatically propagated to the table meta directive.
 * 
 * Automatic synchronization between the table meta and the underlying _p-table_ can be enabled through the
 * `enableTableMetaSync` attribute on the _p-table_ element. This will update the tables state accordingly
 * upon any changes to the table meta.
 * 
 * @see HisTableMetaDirective
 */
@Directive( {
    selector: '[hisTableId], his-table-config, [hisTable], p-table[hisColumnState], p-table[enableTableMetaSync]',
    exportAs: 'hisTableContext',
    standalone: true
} )
export class HisTableContextDirective implements OnInit, OnDestroy {
    private readonly destroyRef = inject( DestroyRef );
    /**
     * Looks for a parent table context to inherit configuration from, if available.
     * Might happen when manually defining an enclosing table context around a table & table-config, or nesting a table inside another. 
     */
    private readonly parentContext = inject( HisTableContextDirective, { skipSelf: true, optional: true } );
    /** Central service for config management */
    private readonly tableService = inject( HisTableService );
    /** Stream for table configuration updates. (needed as a known reference for the `toSignal` conversion at construction time) */
    private readonly internalConfigStream = new ReplaySubject<Readonly<DevTableConfig>>();

    private readonly tableMetaDirective = inject( HisTableMetaDirective, { self: true, optional: true } );

    /** Unique identifier (suffix) for the table associated with this context. ***ONLY AS INPUT! DO NOT READ DIRECTLY*** */
    public readonly hisTableId = input<string>();
    /** Default configuration for the table context. Only applies if this context is the highest ancestor/first encountered context for the associated configuration. */
    public readonly defaultConfig = input<Partial<Omit<DevTableConfigValues, 'defaultTablePageSize'>>>();
    /** Flag to disable persistence of table configuration, even with a table ID. */
    public readonly disablePersist = input( false, { transform: booleanAttribute } );
    /** Flag to disable automatic generation of a table ID using the `hisTableId` as a suffix. */
    public readonly disableTableIdGeneration = input( false, { transform: booleanAttribute } );

    /** Observable stream of the current table configuration, including updates from the internal configuration stream and any inherited parent context. */
    public readonly tableConfigStream = this.prepareTableConfigStream();
    /** Output event for table configuration changes (allows more fine-grained control over table configuration updates in templates) */
    public readonly tableConfigChange = outputFromObservable( this.tableConfigStream );
    /** Signal representing the current table configuration */
    public readonly tableConfig = toSignal( this.tableConfigStream, { initialValue: this.tableService.initDefaultConfig() } );
    
    private readonly effectiveTableId = computed( (): string | symbol => {
        if ( !isBlank( this.hisTableId() ) ) {
            return this.disableTableIdGeneration() ? this.hisTableId() : this.tableService.generateTableId( this.hisTableId() );
        }
        return this.parentContext ? this.parentContext.effectiveTableId() : Symbol('anonymous');
    } );

    ngOnInit() {
        if ( this.isDistinctHighestAncestor() ) {
            this.tableService.registerDirective( this, this.defaultConfig() ).pipe( takeUntilDestroyed( this.destroyRef ) ).subscribe( this.internalConfigStream );
            return;
        }
        console.debug( '[HisTableContextDirective] Nested table context detected. Inheriting table ID & defaults from parent context.' );
        this.parentContext.tableConfigStream.pipe( takeUntilDestroyed( this.destroyRef ) ).subscribe( this.internalConfigStream );
    }

    ngOnDestroy() {
        if ( this.isDistinctHighestAncestor() ) {
            this.tableService.unregisterDirective( this );
        }
    }

    /** Updates the table configuration with the provided values. */
    public updateTableConfig( newConfig: Partial<DevTableConfigValues>, options?: { persist?: boolean } ) {
        const tableId = this.getTableId();
        return this.tableService.patchTableConfig( tableId, newConfig, this.normalizeOptions( options ) );
    }

    /** Sets a specific value in the table configuration. */
    public setTableConfigValue<K extends keyof DevTableConfigValues>( key: K, value: DevTableConfigValues[K], options?: { persist?: boolean } ) {
        const tableId = this.getTableId();
        return this.tableService.setTableConfigValue( tableId, key, value, this.normalizeOptions( options ) );
    }

    /** Resets the table configuration to its default values. */
    public resetTableConfig( options?: { persist?: boolean } ) {
        const tableId = this.getTableId();
        return this.tableService.resetTableConfig( tableId, this.normalizeOptions( options ) );
    }

    /** Retrieves the unique identifier associated with this context. Inherits from parent context if not the highest ancestor. */
    public getTableId(): string | symbol {
        return this.effectiveTableId();
    }

    /** Determines if this context is the highest ancestor with a distinct table ID */
    private isDistinctHighestAncestor() {
        return !this.parentContext || ( !isBlank( this.hisTableId() ) && this.effectiveTableId() !== this.parentContext.effectiveTableId() );
    }

    /**
     * Normalizes the options for persistence, taking into account the `disablePersist` attribute.
     * - If `options.persist` is explicitly set, it takes precedence.
     * - If not set, and `disablePersist` is true, persistence is explicitly disabled to override implicit handling inside the service.
     * - Otherwise, persistence evaluation is left to the service methods.
     */
    private normalizeOptions( options?: { persist?: boolean } ): { persist?: boolean } {
        const {persist = this.disablePersist() === true ? false : undefined} = options || {};
        return { persist };
    }

    /** Prepares the table configuration signal from the configuration stream, adding a tap to update the table meta if available. */
    private prepareTableConfigStream() {
        if ( this.tableMetaDirective ) {
            return this.internalConfigStream.pipe( tap( config => this.tableMetaDirective.setTableConfig( config ) ) );
        }
        return this.internalConfigStream.asObservable();
    }
}